import React, { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import produce from 'immer';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { DatasetSelectionProvider, FileSelectionProvider, NotebookPropertiesProvider, } from '../../App/ApplicationContext';
import { ResultsWindow } from './ResultsWindow';
import { ChipsBar } from './ChipsBar';
import FacetSelector from './FacetSelector';
import FacetSelectionSideBar from '../Sidebar/FacetSelectionSideBar';
import { SearchFooter, PROJECTFACET } from '../Footer/SearchFooter';
import { SearchHeader } from '../Header/SearchHeader';
import { NodeSelection } from '../Header/NodeSelection';
import { ProjectSelection } from '../Header/ProjectSelection';
import { ESGFNodeList } from '../../../config/customSelections/datanodes';
import { defaultProjects } from '../../../config/projects';
import { getFacetsInfo } from '../../../utils/getFacetsInfo';
import { OPERATION, SUBSETTING, } from '../../../types/searchTypes';
const Alert = (props) => {
    return React.createElement(MuiAlert, Object.assign({ elevation: 6, variant: 'filled' }, props));
};
const classes = {
    root: {
        background: '#fff',
        display: 'grid',
        gridGap: '0px',
        gridTemplateRows: '1fr 46px',
        gridTemplateColumns: '1fr',
        gridTemplateAreas: "'facetSelector' 'facetSelectorFooter'",
        height: '100%',
        width: '100%',
        overflow: 'hidden',
    },
    main: {
        width: '100%',
        boxShadow: '0px 0px 4px 1px #BDBDBD',
    },
    facetSelector: {
        marginTop: '45px',
        background: '#fff',
        gridArea: 'facetSelector',
        overflowY: 'auto',
        display: 'flex',
    },
    backdrop: {
        zIndex: 1201,
        color: '#fff',
        backgroundColor: 'rgba(128, 128, 128,0.2)',
    },
};
const DEFAULTNODES = ESGFNodeList;
const TailoredSearch = ({ user }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const projectParam = searchParams.get('project') || 'CMIP6';
    const navigate = useNavigate();
    const location = useLocation();
    // if searchParams not set - redirce
    useEffect(() => {
        if (!searchParams.get('project')) {
            // force set search param
            setSearchParams((params) => {
                params.set('project', projectParam);
                return params;
            });
            // force redirect
            navigate(`${location.pathname}?${searchParams.toString()}`, {
                replace: true,
            });
        }
    }, [searchParams, projectParam]);
    const project = defaultProjects.find((p) => p.title === projectParam) ||
        {};
    const [selectedFacet, setSelectedFacet] = React.useState((project && project.mainFacets[0]) || {});
    const [searchQuery, setSearchQuery] = React.useState({
        selectedItems: [
            {
                facetId: PROJECTFACET,
                fieldId: defaultProjects[0].facetName,
                include: true,
            },
        ],
    });
    useEffect(() => {
        var _a;
        // Flush facet selection on project change
        if (searchQuery &&
            project.title !==
                ((_a = searchQuery.selectedItems.find((item) => item.facetId === PROJECTFACET)) === null || _a === void 0 ? void 0 : _a.fieldId)) {
            setSearchQuery({
                selectedItems: [
                    {
                        facetId: PROJECTFACET,
                        fieldId: project.facetName,
                        include: true,
                    },
                ],
            });
        }
    }, [project && project.facetName]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [selectedNodes, setSelectedNodes] = React.useState(DEFAULTNODES);
    const [openNodeSelection, setOpenNodeSelection] = React.useState(false);
    const [openProjectSelection, setOpenProjectSelection] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState();
    const [resultsWindowState, setResultsWindow] = React.useState({
        open: false,
        operation: OPERATION.DOWNLOAD,
        subsettingMode: SUBSETTING.NONE,
    });
    // Simple view: Facets subselection as in config, Advanced: All facets available on node
    const [enableAdvancedView, setEnableAdvancedView] = React.useState(false);
    const [searchFacetResults, setSearchFacetResults] = React.useState(null);
    const [searchMask, setSearchMask] = React.useState(null);
    React.useEffect(() => {
        if (project) {
            setIsLoading(true);
            setErrorMessage(null);
            getFacetsInfo(searchQuery, project, selectedNodes)
                .then((result) => {
                setSearchFacetResults(result);
                // set searchMask if is initial search:
                // SearchQuery only contains project and no other facetIds
                if (searchQuery.selectedItems.length === 1 &&
                    searchQuery.selectedItems[0].facetId === 'project') {
                    setSearchMask(result);
                }
            })
                .catch((e) => {
                console.error(e);
                setErrorMessage(`Unable to connect to search service (${e})`);
            })
                .finally(() => {
                setIsLoading(false);
            });
        }
    }, [searchQuery, selectedNodes]); // use Effect if nodes or facets change
    return (React.createElement("div", { style: { height: '100%', display: 'block' } },
        React.createElement(DatasetSelectionProvider, null,
            React.createElement(FileSelectionProvider, null,
                React.createElement(NotebookPropertiesProvider, null,
                    React.createElement("main", { style: selectedFacet
                            ? classes.root
                            : Object.assign(Object.assign({}, classes.root), { gridTemplateRows: '1fr' }) },
                        React.createElement(SearchHeader, Object.assign({}, {
                            selectedNodes,
                            enableAdvancedView,
                            setEnableAdvancedView,
                            openProjectSelection,
                            setOpenProjectSelection,
                            openNodeSelection,
                            setOpenNodeSelection,
                            project,
                        })),
                        React.createElement("div", { style: classes.facetSelector },
                            React.createElement(Backdrop, { sx: classes.backdrop, open: isLoading },
                                React.createElement(CircularProgress, null)),
                            React.createElement(Snackbar, { open: errorMessage !== null, autoHideDuration: 6000, onClose: () => {
                                    setErrorMessage(null);
                                } },
                                React.createElement("div", null,
                                    React.createElement(Alert, { onClose: () => {
                                            setErrorMessage(null);
                                        }, severity: 'error' }, errorMessage && (React.createElement("div", null, JSON.stringify(errorMessage)))))),
                            resultsWindowState.open && (React.createElement(ResultsWindow, { searchQuery: searchQuery, projectType: project, nodes: selectedNodes, operation: resultsWindowState.operation, subsettingMode: resultsWindowState.subsettingMode, user: user, handleClose: () => {
                                    setResultsWindow((prevState) => (Object.assign(Object.assign({}, prevState), { open: false })));
                                } })),
                            React.createElement(FacetSelectionSideBar, { projectType: project, onFacetClick: (facet) => {
                                    setSelectedFacet(facet);
                                }, searchQuery: searchQuery }),
                            React.createElement("div", { style: classes.main },
                                React.createElement(ProjectSelection, { openProjectSelection: openProjectSelection, setOpenProjectSelection: setOpenProjectSelection }),
                                React.createElement(NodeSelection, { selectedNodes: selectedNodes, setNodes: setSelectedNodes, openNodeSelection: openNodeSelection, setOpenNodeSelection: setOpenNodeSelection }),
                                React.createElement(ChipsBar, { searchQuery: searchQuery, removeFacetField: (facetId, fieldId) => {
                                        const selectedItems = searchQuery.selectedItems.filter((o) => o.facetId !== facetId || o.fieldId !== fieldId);
                                        setSearchQuery(produce(searchQuery, (draft) => {
                                            // eslint-disable-next-line no-param-reassign
                                            draft.selectedItems = selectedItems;
                                        }));
                                        if (selectedItems.length === 0) {
                                            setSelectedFacet({});
                                        }
                                    } }),
                                React.createElement(FacetSelector, { facetType: selectedFacet, searchFacetResults: searchFacetResults, searchResultMask: searchMask, enableAdvancedView: enableAdvancedView, searchQuery: searchQuery, changeFacetField: (facetId, fieldId, checked) => {
                                        setSearchQuery(produce(searchQuery, (draft) => {
                                            if (checked) {
                                                draft.selectedItems.push({
                                                    facetId,
                                                    fieldId,
                                                    include: true,
                                                });
                                            }
                                            else {
                                                // eslint-disable-next-line no-param-reassign
                                                draft.selectedItems =
                                                    searchQuery.selectedItems.filter((o) => o.facetId !== facetId || o.fieldId !== fieldId);
                                            }
                                        }));
                                    } }))),
                        React.createElement(SearchFooter, Object.assign({}, {
                            user,
                            setResultsWindow,
                            project,
                            selectedNodes,
                            searchQuery,
                        }))))))));
};
export default TailoredSearch;
